import { useQuery } from '@tanstack/react-query';
import { useMedplum } from '@medplum/react';
import type { CarePlan, Parameters, Patient } from '@medplum/fhirtypes';

import { PROGRAMS } from '@/features/shared/constants/reference-data';
import { medplum as medplumClient } from '@/config/medplum';

export type PatientProgram = {
  cpid: string;
  episodeOfCareId?: string;
  managingOrganizationId?: string;
  programCode: string;
  programName: string;
  startDate?: string;
  endDate?: string;
  status: CarePlan['status'];
  practiceName?: string;
  orgName?: string;
  providerName?: string;
  providerRoleId?: string;
  timezone?: string;
};

export type CarePlansData = {
  patient: Patient;
  programs: PatientProgram[];
};

const parseCarePlansResponse = (response: Parameters): CarePlansData => {
  const patient = response.parameter?.find((p) => p.name === 'patient')?.resource as Patient;

  const programs = (response.parameter ?? [])
    .filter((p) => p.name === 'carePlan')
    .map((p) => {
      const parts = p.part ?? [];
      const getPart = (name: string) => parts.find((part) => part.name === name)?.valueString;
      const programCode = getPart('programCode') ?? '';
      const program = PROGRAMS.find((pr) => pr.code === programCode);

      return {
        cpid: getPart('cpid') ?? '',
        episodeOfCareId: getPart('episodeOfCareId'),
        managingOrganizationId: getPart('managingOrganizationId'),
        programCode,
        programName: program?.display ?? getPart('programName') ?? programCode,
        startDate: getPart('startDate'),
        endDate: getPart('endDate'),
        status: (getPart('status') ?? 'active') as CarePlan['status'],
        practiceName: getPart('practiceName'),
        orgName: getPart('orgName'),
        providerName: getPart('providerName'),
        providerRoleId: getPart('providerRoleId'),
        timezone: getPart('timezone'),
      };
    });

  return { patient, programs };
};

const fetchCarePlans = async (patientId: string): Promise<CarePlansData> => {
  const response = (await medplumClient.post(
    medplumClient.fhirUrl('Patient', '$care-plans').toString(),
    {
      resourceType: 'Parameters',
      parameter: [{ name: 'patientId', valueString: patientId }],
    },
  )) as Parameters;

  return parseCarePlansResponse(response);
};

export const getPatientCarePlansQueryKey = (patientId: string) => [
  'CarePlan',
  'patient',
  patientId,
];

export const fetchPatientCarePlans = fetchCarePlans;

export const usePatientCarePlans = (patientId: string | undefined) => {
  const medplum = useMedplum();

  return useQuery({
    queryKey: getPatientCarePlansQueryKey(patientId ?? ''),
    queryFn: async (): Promise<CarePlansData> => {
      const response = (await medplum.post(medplum.fhirUrl('Patient', '$care-plans').toString(), {
        resourceType: 'Parameters',
        parameter: [{ name: 'patientId', valueString: patientId }],
      })) as Parameters;

      return parseCarePlansResponse(response);
    },
    enabled: !!patientId,
  });
};
